import { useMutation, useQuery } from "@apollo/client";
import { useMemo } from "react";
import { createKeywordGroup } from "../graphql/mutations/createKeywordGroup";
import { deleteKeywordGroup } from "../graphql/mutations/deleteKeywordGroup";
import { updateKeywordGroup } from "../graphql/mutations/updateKeywordGroup";
import { listProductGroups } from "../graphql/queries/listProductGroups";

const PRODUCT_GROUP_TYPE = "PRODUCT_GROUP";

const generateSlug = (name) =>
  (name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

export const useProductGroups = () => {
  const { data, loading, error, refetch } = useQuery(listProductGroups, {
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });
  const [executeCreate, { loading: creating }] = useMutation(createKeywordGroup);
  const [executeUpdate] = useMutation(updateKeywordGroup);
  const [executeDelete] = useMutation(deleteKeywordGroup);

  // Full product group objects (id, name, etc.)
  const productGroupsFull = useMemo(() => {
    return (data?.listKeywordGroups || []).filter((g) => !g.deleted);
  }, [data]);

  // Dropdown-friendly list
  const productGroups = useMemo(() => {
    return productGroupsFull.map((group) => ({
      id: group.id,
      displayText: group.name,
    }));
  }, [productGroupsFull]);

  // Map of id -> name for quick lookups
  const productGroupMap = useMemo(() => {
    const map = {};
    productGroupsFull.forEach((g) => {
      map[g.id] = g.name;
    });
    return map;
  }, [productGroupsFull]);

  const createProductGroup = async (name) => {
    const result = await executeCreate({
      variables: {
        input: {
          name,
          slug: generateSlug(name),
          type: PRODUCT_GROUP_TYPE,
          keywords: [name],
        },
      },
    });
    refetch();
    return result;
  };

  const renameProductGroup = async (id, newName) => {
    const existing = productGroupsFull.find((g) => g.id === id);
    const result = await executeUpdate({
      variables: {
        id,
        input: {
          name: newName,
          slug: generateSlug(newName),
          type: PRODUCT_GROUP_TYPE,
          keywords: existing?.keywords || [newName],
        },
      },
    });
    refetch();
    return result;
  };

  const deleteProductGroup = async (id) => {
    const result = await executeDelete({ variables: { id } });
    refetch();
    return result;
  };

  return {
    productGroups,
    productGroupsFull,
    productGroupMap,
    loading,
    error,
    refetch,
    creating,
    createProductGroup,
    renameProductGroup,
    deleteProductGroup,
  };
};
